import { useContext, useRef, useState } from 'react';

import { Button } from '@selina-finance/ui';
import cookie from 'cookie';
import { Form, Formik } from 'formik';
import mixpanel from 'mixpanel-browser';
import { useLocation, useNavigate } from 'react-router-dom';

import { getDefaultValues, getValidationSchema } from '@Shared/helpers/input';
import { ApplicationContext } from '@src/app/contexts/Application';
import { formItems } from '@src/app/formModels/auth/createUser';
import { handleAuthResult } from '@src/shared/helpers/auth';
import { FormError } from '@src/ui/components/FormError';
import FileLockIcon from '@src/ui/components/Icons/FileLock';

const defaultValues = getDefaultValues(formItems);
const validationSchema = getValidationSchema(formItems);

export interface CreateUserViewProps {
    className?: string;
    email: string;
    register: Function;
    buttonText: string;
    onClose?: () => void;
}

const CreateUserView = ({
    className,
    email,
    register,
    buttonText,
    onClose,
}: CreateUserViewProps) => {
    const [errors, setErrors] = useState({});
    const [showForm, setShowForm] = useState(true);
    const [loading, setLoading] = useState(false);
    const formikRef = useRef<any>(null);
    const location = useLocation();

    const cookies = cookie.parse(document.cookie);
    const appInfo = cookies.application ? JSON.parse(cookies.application) : {};

    const { dashboardItemStatuses } = useContext(ApplicationContext);

    const handleSubmit = async (values: any) => {
        setLoading(true);
        setErrors({});

        const applicationId =
            appInfo.emailAddress === values.email
                ? appInfo.applicationId
                : null;

        const result = await register(values.email, applicationId);

        handleAuthResult(result, values, setErrors, () => setShowForm(false));

        setLoading(false);
    };

    const navigate = useNavigate();

    const handleNavigate = () => {
        const latestStep = dashboardItemStatuses.find(
            (item: { completed: boolean }) => !item.completed,
        );

        const stage = location?.pathname?.split('/').reverse()[0];

        mixpanel.track('tapped_continue_now_fact_find');

        if (latestStep.name === stage || stage === `${latestStep.name}2`) {
            onClose?.();
        } else {
            navigate(`/eligibility/${latestStep.name}`);
        }
    };

    const handleLogoutExit = () => {
        mixpanel.track('tapped_log_out_fact_find');

        navigate('/logout');
    };

    return (
        <div className={className}>
            <Formik
                enableReinitialize
                initialValues={{ ...defaultValues, email: email }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={(ref) => (formikRef.current = ref)}
            >
                {(formikProps) => {
                    return (
                        <>
                            {showForm && (
                                <>
                                    <Form autoComplete='off'>
                                        <input
                                            title='email'
                                            name='email'
                                            type='hidden'
                                            value={formikProps.values.email}
                                        />
                                        <p className='text-xl leading-6 mb-5'>
                                            Create an account to complete your
                                            application later.
                                        </p>
                                        <FormError otherErrors={errors} />
                                        <Button
                                            type='submit'
                                            className='mt-6 w-full'
                                            color='secondary'
                                            size='large'
                                            disabled={loading}
                                            onClick={() =>
                                                mixpanel.track(
                                                    'tapped_create_account_fact_find',
                                                )
                                            }
                                        >
                                            {loading ? (
                                                <div className='flex items-center justify-center'>
                                                    <div className='animate-spin h-4 w-4 mr-3 border-l border-t-transparent border-white rounded-full' />
                                                    Creating account...
                                                </div>
                                            ) : buttonText ? (
                                                buttonText
                                            ) : (
                                                'Create account'
                                            )}
                                        </Button>
                                    </Form>
                                    <div className='text-base_light text-left text-base leading-tight'>
                                        <br />
                                        By signing up you verify that you have
                                        read and agree with Selina Finance's{' '}
                                        <a
                                            target='_blank'
                                            href='https://www.selinafinance.co.uk/terms-of-use'
                                            rel='noreferrer'
                                            className='underline'
                                        >
                                            terms and conditions
                                        </a>{' '}
                                        and{' '}
                                        <a
                                            target='_blank'
                                            href='https://www.selinafinance.co.uk/terms-of-use'
                                            rel='noreferrer'
                                            className='underline'
                                        >
                                            privacy policy
                                        </a>
                                        <br />
                                        <br />
                                    </div>
                                </>
                            )}
                            {!showForm && (
                                <Form className='my-2'>
                                    <div className='flex justify-between'>
                                        <FileLockIcon />
                                        <p className='ml-3 text-2xl font-semibold leading-8'>
                                            Thanks, your loan application has
                                            been saved.
                                        </p>
                                    </div>
                                    <p className='text-xl leading-6 mt-2'>
                                        We’ve created your account, you can log
                                        in at anytime using the email address
                                        you provided.
                                    </p>
                                    <div className='mb-6 mt-10'>
                                        <Button
                                            type='button'
                                            className='mt-6 w-full'
                                            color='secondary'
                                            size='large'
                                            onClick={async () =>
                                                handleNavigate()
                                            }
                                        >
                                            {buttonText
                                                ? buttonText
                                                : 'Continue my application now'}
                                        </Button>
                                        <Button
                                            type='button'
                                            className='mt-2 w-full'
                                            color='outline-dark'
                                            size='large'
                                            onClick={handleLogoutExit}
                                        >
                                            {buttonText
                                                ? buttonText
                                                : 'Log out and exit'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CreateUserView;
